/**
 * Resolves which colour scheme the document uses.
 *
 * The dark token set is keyed on `[data-theme='dark']` and nothing sets that
 * attribute on its own, so without this the dark theme is unreachable no matter
 * what the reader's OS preference is.
 *
 * Why an attribute rather than a `@media (prefers-color-scheme: dark)` block:
 * the dark values would have to be duplicated into the media block, and
 * `tokens.css.test.ts` verifies exactly one authoritative dark block. Two
 * copies drift. One attribute also gives the reader a manual override later.
 *
 * Why a module rather than an inline <script> in index.html: an inline script
 * would force `'unsafe-inline'` (or nonce plumbing) into the CSP that §7.14
 * requires. Loading this as the first import in the composition root costs a
 * brief flash of the light canvas at worst.
 */

export type ColorScheme = 'light' | 'dark';
export type ColorSchemePreference = ColorScheme | 'system';

const STORAGE_KEY = 'ar-rasheed:color-scheme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

/**
 * What someone sees before they choose anything: whatever their own device is
 * already set to.
 *
 * Most of these 872 people will open this on a phone, often outdoors, and the
 * device already knows which they can read. An explicit choice overrides it and
 * persists; the switch is on every screen, including before sign-in.
 */
const DEFAULT_PREFERENCE: ColorSchemePreference = 'system';

function readStoredPreference(): ColorSchemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
    return DEFAULT_PREFERENCE;
  } catch {
    // Private browsing and some embedded webviews throw on storage access.
    return DEFAULT_PREFERENCE;
  }
}

function systemPrefers(): ColorScheme {
  return globalThis.matchMedia?.(DARK_QUERY).matches ? 'dark' : 'light';
}

export function resolveColorScheme(preference: ColorSchemePreference): ColorScheme {
  return preference === 'system' ? systemPrefers() : preference;
}

export function applyColorScheme(scheme: ColorScheme): void {
  document.documentElement.setAttribute('data-theme', scheme);
}

export function storeColorSchemePreference(preference: ColorSchemePreference): void {
  try {
    // Stored even when it is `'system'`: an empty key now means "has not
    // chosen", which resolves to dark, so removing it would silently undo a
    // deliberate choice to follow the OS.
    localStorage.setItem(STORAGE_KEY, preference);
  } catch {
    // Preference simply does not persist; the applied scheme is unaffected.
  }
}

/**
 * Applies the stored preference, or follows the OS while nobody has chosen.
 *
 * @returns an unsubscribe function. §11.5.1 forbids listeners without one.
 */
export function initColorScheme(): () => void {
  const preference = readStoredPreference();
  applyColorScheme(resolveColorScheme(preference));

  const media = globalThis.matchMedia?.(DARK_QUERY);
  if (!media) return () => undefined;

  const onChange = (event: MediaQueryListEvent) => {
    // An explicit choice wins over the OS flipping at sunset.
    if (readStoredPreference() !== 'system') return;
    applyColorScheme(event.matches ? 'dark' : 'light');
  };

  media.addEventListener('change', onChange);
  return () => {
    media.removeEventListener('change', onChange);
  };
}
